import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import {
  formatSpecialDate,
  getNextContestNumber,
  type PoolScope,
} from "@/lib/check-scope";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lotterySlug: string;
  initialScope?: PoolScope;
  title?: string;
  confirmLabel?: string;
  onConfirm: (payload: {
    scope: PoolScope;
    from: number | null;
    until: number | null;
    nextContest: number;
    specialScheduleId: string | null;
    special?: null;
  }) => Promise<void> | void;
};

export function PoolScopeDialog({
  open,
  onOpenChange,
  lotterySlug,
  initialScope = "next_only",
  title = "Alterar concurso do bolão",
  confirmLabel = "Confirmar",
  onConfirm,
}: Props) {
  const [nextContest, setNextContest] = useState<number | null>(null);
  const [contestNumber, setContestNumber] = useState<number | null>(null);
  const [confirmedMove, setConfirmedMove] = useState(false);
  const [scheduled, setScheduled] = useState<Array<{ contest_number: number | null; draw_date: string; is_special: boolean; special_name: string | null; id: string }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContestNumber(null);
    setConfirmedMove(false);
    let cancelled = false;
    getNextContestNumber(lotterySlug).then((n) => {
      if (!cancelled) setNextContest(n);
    });
    supabase.from("lottery_schedule").select("id, contest_number, draw_date, is_special, special_name, lotteries!inner(slug)")
      .eq("lotteries.slug", lotterySlug).order("contest_number").limit(100)
      .then(({ data }) => { if (!cancelled) setScheduled((data ?? []).filter((row) => row.contest_number != null)); });
    return () => { cancelled = true; };
  }, [open, lotterySlug, initialScope]);

  const handleConfirm = async () => {
    if (nextContest == null || contestNumber == null || contestNumber < nextContest) return;
    if (!confirmedMove) {
      setConfirmedMove(true);
      return;
    }
    const choice = scheduled.find((row) => row.contest_number === contestNumber);
    setBusy(true);
    try {
      await onConfirm({
        scope: choice?.is_special ? "special" : "next_only",
        from: contestNumber,
        until: contestNumber,
        nextContest,
        specialScheduleId: choice?.is_special ? choice.id : null,
        special: null,
      });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const n = nextContest;
  const choices = scheduled.filter((row) => row.contest_number != null && row.contest_number >= (n ?? Infinity));
  if (n != null && !choices.some((row) => row.contest_number === n)) {
    choices.unshift({ id: "", contest_number: n, draw_date: "", is_special: false, special_name: null });
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{title}</DialogTitle>
          <DialogDescription>
            O concurso escolhido passa a valer para todos os jogos do bolão. Próximo: {n != null ? <strong>#{n}</strong> : "..."}.
          </DialogDescription>
        </DialogHeader>
        <Select value={contestNumber != null ? String(contestNumber) : undefined} onValueChange={(v) => { setContestNumber(Number(v)); setConfirmedMove(false); }}>
          <SelectTrigger><SelectValue placeholder="Escolha um concurso" /></SelectTrigger>
          <SelectContent>
            {choices.map((row) => <SelectItem key={row.contest_number} value={String(row.contest_number)}>
              Concurso #{row.contest_number}{row.is_special ? ` — ${row.special_name ?? "Especial"}` : ""}
              {row.draw_date ? ` (${formatSpecialDate(row.draw_date)})` : ""}
            </SelectItem>)}
          </SelectContent>
        </Select>
        {confirmedMove && <p className="text-sm text-destructive">Todos os jogos do bolão passarão a valer apenas para o concurso #{contestNumber}. Confirme novamente para continuar.</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={busy || nextContest == null || contestNumber == null}>{busy ? "Salvando..." : confirmedMove ? "Sim, mudar todos os jogos" : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}